// Health & Resources — plain-language, stigma-free, factual STI education.
// Content is educational only, not medical advice. Every topic ends with a
// standardized disclaimer rendered in HealthTopic.jsx.
//
// Canadian sources: prefer canada.ca / Public Health Agency of Canada (PHAC).

export const TOPICS = [
  {
    id: "hsv",
    slug: "hsv",
    title: "HSV-1 & HSV-2 (Herpes)",
    subtitle: "Common, manageable, and no reason for shame",
    kicker: "Herpes",
    icon: "Sparkles",
    tone: "rose",
    hubBlurb:
      "Understand HSV-1 and HSV-2, outbreaks, asymptomatic shedding, antiviral treatment, disclosure, and dating with herpes.",
    sections: [
      {
        heading: "What it is",
        body:
          "Herpes simplex virus (HSV) is a common viral infection. HSV-1 is most often associated with oral cold sores but can also cause genital herpes. HSV-2 is most often associated with genital herpes. Both types are lifelong infections that stay in the nerve cells, but symptoms — when they appear at all — can usually be managed well.",
      },
      {
        heading: "Common symptoms",
        body:
          "When symptoms show up, they can include tingling, itching, or burning followed by small blisters or sores that scab over and heal in 1–3 weeks. The first outbreak is usually the most intense; later outbreaks tend to be milder and shorter. Some people also feel flu-like symptoms during a first outbreak.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "A large share of people with HSV never notice symptoms, or mistake them for something else (like an ingrown hair or razor burn). Not having symptoms does not mean a person cannot pass HSV to someone else.",
      },
      {
        heading: "How it spreads",
        body:
          "HSV spreads through skin-to-skin contact with an infected area. It can spread during a visible outbreak and also through asymptomatic viral shedding, when the skin looks normal. HSV-1 also spreads through oral contact (e.g., kissing) and can be passed to the genitals through oral sex.",
      },
      {
        heading: "Testing",
        body:
          "HSV can be tested by swabbing an active sore (PCR or viral culture) or by a blood test that looks for antibodies. Routine STI screening in Canada does not usually include HSV blood testing unless there is a specific reason. If you think you may have HSV, talk to a healthcare provider or a sexual health clinic about what test is right for you.",
      },
      {
        heading: "Treatment and management",
        body:
          "There is no cure, but antiviral medicines like acyclovir, valacyclovir, and famciclovir can shorten outbreaks, ease symptoms, and lower the chance of passing HSV to a partner. Some people take antivirals only during outbreaks; others take a small dose every day (suppressive therapy). A healthcare provider can help decide what fits your situation.",
      },
      {
        heading: "Prevention",
        body:
          "Ways to lower the risk of passing HSV include: taking daily suppressive antivirals, avoiding sex during an outbreak or when symptoms are starting, using condoms and dental dams (they help but don't cover all skin), and having open conversations with partners.",
      },
      {
        heading: "When to seek medical care",
        body:
          "See a healthcare provider if you have new genital sores, a first suspected outbreak, very frequent or severe outbreaks, sores that don't heal, or if you are pregnant and have HSV (there are specific steps to protect the baby). Anyone with a weakened immune system should also seek care promptly.",
      },
      {
        heading: "Dating and disclosure",
        body:
          "Millions of people date, love, marry, and have healthy families while living with HSV. Telling a partner is a personal choice about timing and honesty, not an apology. Many people find it helps to share matter-of-factly, offer accurate information, and give the other person time to ask questions. HerpMates exists so this conversation is easier — every person here already gets it.",
      },
    ],
    sources: [
      { label: "Government of Canada — Genital herpes", url: "https://www.canada.ca/en/public-health/services/diseases/genital-herpes.html" },
      { label: "Public Health Agency of Canada — STI guides for professionals", url: "https://www.canada.ca/en/public-health/services/infectious-diseases/sexual-health-sexually-transmitted-infections/canadian-guidelines.html" },
    ],
  },

  {
    id: "hiv",
    slug: "hiv",
    title: "HIV",
    subtitle: "Testing, treatment, PrEP, PEP, and U=U",
    kicker: "HIV",
    icon: "Ribbon",
    tone: "red",
    hubBlurb:
      "What HIV is and isn't, modern treatment, prevention with PrEP and PEP, and why U=U (Undetectable = Untransmittable) matters.",
    sections: [
      {
        heading: "What HIV is",
        body:
          "HIV (human immunodeficiency virus) is a virus that affects the immune system. HIV is not the same thing as AIDS. AIDS is an advanced stage of untreated HIV. With modern treatment, people living with HIV do not develop AIDS and can live full, long lives.",
      },
      {
        heading: "Common symptoms",
        body:
          "Some people have a short flu-like illness (fever, sore throat, swollen glands, rash) 2–4 weeks after infection. Many people have no early symptoms at all. Without treatment, HIV slowly damages the immune system over years, which is why testing is important even when you feel fine.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "You cannot tell if someone has HIV — or if you have it — by how you feel or look. The only way to know is to test. Regular testing is a normal part of sexual health.",
      },
      {
        heading: "How it spreads",
        body:
          "HIV is passed through specific body fluids: blood, semen, pre-seminal fluid, rectal fluid, vaginal fluid, and breast/chest milk. The most common routes are unprotected anal or vaginal sex and sharing needles or drug equipment. HIV is not spread through saliva, sweat, tears, hugs, sharing utensils, toilets, or casual contact.",
      },
      {
        heading: "Testing",
        body:
          "HIV tests are quick, confidential, and widely available in Canada, including free clinics and pharmacy self-test kits. Depending on the test, results can be ready in minutes to a few days. If you've had a possible exposure, testing may need to be repeated because there is a window period before HIV shows up on a test.",
      },
      {
        heading: "Treatment",
        body:
          "HIV is treated with antiretroviral therapy (ART) — usually one or two pills a day, or a long-acting injection in some cases. Treatment lowers the amount of HIV in the body to very low levels and keeps the immune system healthy. Most people on ART reach an undetectable viral load within a few months.",
      },
      {
        heading: "U=U — Undetectable = Untransmittable",
        body:
          "A person living with HIV who is on effective treatment and maintains an undetectable viral load cannot pass HIV to sexual partners. This is called U=U. It is backed by large scientific studies and endorsed by public health agencies. U=U changes everything about how HIV is understood today.",
      },
      {
        heading: "PrEP — pre-exposure prophylaxis",
        body:
          "PrEP is a daily pill (or, for some people, an injection) taken by an HIV-negative person to prevent getting HIV. When taken as prescribed, PrEP is highly effective. PrEP is available in Canada through a healthcare provider. It's a good option for anyone at higher risk of HIV exposure.",
      },
      {
        heading: "PEP — post-exposure prophylaxis",
        body:
          "PEP is emergency HIV medicine taken after a possible exposure. It must be started within 72 hours (the sooner the better) and taken for 28 days. PEP is available at emergency rooms, sexual health clinics, and some walk-in clinics. If you think you may have been exposed to HIV, seek care immediately.",
      },
      {
        heading: "Prevention",
        body:
          "Along with PrEP and PEP: condoms lower risk significantly, sterile needles and equipment prevent transmission through drug use, and treatment (for people living with HIV) prevents onward transmission via U=U.",
      },
      {
        heading: "When to seek medical care",
        body:
          "Get tested regularly if you're sexually active. Seek care right away if you've had a possible exposure (PEP), if you're pregnant and living with HIV (treatment prevents transmission to the baby), or if you want to start PrEP.",
      },
      {
        heading: "Dating and disclosure",
        body:
          "People living with HIV date, marry, and raise families. In Canada, disclosure laws exist and can be complex — a sexual health clinic or organization like CATIE can walk you through what applies to your situation. On the human side: honesty, information, and time make hard conversations easier.",
      },
    ],
    sources: [
      { label: "Government of Canada — HIV and AIDS", url: "https://www.canada.ca/en/public-health/services/diseases/hiv-aids.html" },
      { label: "CATIE — Canada's source for HIV and hepatitis C information", url: "https://www.catie.ca/" },
      { label: "Government of Canada — Pre-exposure prophylaxis (PrEP)", url: "https://www.canada.ca/en/public-health/services/diseases/hiv-aids/pre-exposure-prophylaxis.html" },
    ],
  },

  {
    id: "chlamydia",
    slug: "chlamydia",
    title: "Chlamydia",
    subtitle: "The most reported STI in Canada — and easy to treat",
    kicker: "Bacterial STI",
    icon: "Bug",
    tone: "amber",
    hubBlurb:
      "One of the most commonly reported STIs in Canada. Often silent, easy to test for, and cured with antibiotics.",
    sections: [
      {
        heading: "What it is",
        body:
          "Chlamydia is a bacterial infection caused by Chlamydia trachomatis. It is one of the most commonly reported STIs in Canada. Because it often has no symptoms, many people don't know they have it — which is why regular screening matters.",
      },
      {
        heading: "Common symptoms",
        body:
          "When symptoms do show up, they may include unusual discharge from the vagina or penis, burning when peeing, pain during sex, pelvic pain, or (from anal sex) rectal discomfort. Symptoms usually appear 1–3 weeks after infection, if at all.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "Most people with chlamydia — especially in the vagina, throat, or rectum — feel completely fine. Untreated chlamydia can still cause problems later (like pelvic inflammatory disease or infertility), so testing is the only reliable way to know.",
      },
      {
        heading: "How it spreads",
        body:
          "Chlamydia spreads through vaginal, anal, or oral sex with someone who has it. It can also be passed from a pregnant person to their baby during birth.",
      },
      {
        heading: "Testing",
        body:
          "Testing is simple: a urine sample or a swab (vaginal, throat, or rectal, depending on the type of sex you have). Home test kits and clinic testing are widely available in Canada. Ask for testing at all sites where you've had sex — a urine test alone can miss throat and rectal infections.",
      },
      {
        heading: "Treatment",
        body:
          "Chlamydia is cured with antibiotics — usually a single dose or a short course of pills. It's important to finish the full course, avoid sex for 7 days after finishing treatment, and make sure recent partners are also treated so you don't get re-infected.",
      },
      {
        heading: "Prevention",
        body:
          "Condoms and dental dams lower the risk significantly. Regular STI testing (at least yearly if you're sexually active, more often if you have new partners) helps catch and treat infections early.",
      },
      {
        heading: "When to seek medical care",
        body:
          "See a healthcare provider if you have symptoms, if a partner tests positive, or as part of routine screening. If you have pelvic pain, fever, or bleeding, get seen sooner.",
      },
      {
        heading: "Talking with partners",
        body:
          "If you test positive, sexual partners from the last 60 days (or the most recent partner if longer) should be told so they can test and treat too. Many clinics offer anonymous partner notification if that feels easier.",
      },
    ],
    sources: [
      { label: "Government of Canada — Chlamydia", url: "https://www.canada.ca/en/public-health/services/diseases/chlamydia.html" },
    ],
  },

  {
    id: "gonorrhea",
    slug: "gonorrhea",
    title: "Gonorrhea",
    subtitle: "Curable — but antibiotic resistance is on the rise",
    kicker: "Bacterial STI",
    icon: "Bug",
    tone: "amber",
    hubBlurb:
      "A bacterial STI that's curable with antibiotics. Rising rates and antibiotic resistance make prompt testing and treatment important.",
    sections: [
      {
        heading: "What it is",
        body:
          "Gonorrhea is a bacterial infection caused by Neisseria gonorrhoeae. It can infect the genitals, rectum, or throat. Rates have been rising in Canada, and some strains have become harder to treat, so early testing matters.",
      },
      {
        heading: "Common symptoms",
        body:
          "When present, symptoms may include thick or discoloured discharge (from the penis or vagina), burning when peeing, painful or swollen testicles, painful sex, bleeding between periods, or a sore throat. Rectal infections can cause discharge, itching, or pain.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "Many gonorrhea infections — especially in the throat and rectum — have no symptoms at all. You can pass it on without knowing you have it.",
      },
      {
        heading: "How it spreads",
        body:
          "Through vaginal, anal, or oral sex. It can also pass from a pregnant person to their baby during birth. It is not spread by casual contact, toilets, or sharing dishes.",
      },
      {
        heading: "Testing",
        body:
          "Testing uses a urine sample or a swab (throat, rectal, vaginal). Ask for tests at all sites where you have sex. Testing is free and confidential at most sexual health clinics in Canada.",
      },
      {
        heading: "Treatment",
        body:
          "Gonorrhea is treated with antibiotics, usually one injection plus a pill. Because of antibiotic resistance, treatment is guided by up-to-date medical recommendations. Retesting after treatment is often recommended to make sure the infection is gone.",
      },
      {
        heading: "Prevention",
        body:
          "Condoms and dental dams reduce risk. Regular testing catches infections early — especially important if you have new or multiple partners.",
      },
      {
        heading: "When to seek medical care",
        body:
          "See a provider if you have symptoms, if a partner tests positive, or if you're due for a screen. Untreated gonorrhea can spread and cause pelvic inflammatory disease, infertility, or other complications.",
      },
      {
        heading: "Talking with partners",
        body:
          "Recent partners (usually from the last 60 days) should be told so they can also test and treat. Clinics can help with anonymous partner notification.",
      },
    ],
    sources: [
      { label: "Government of Canada — Gonorrhea", url: "https://www.canada.ca/en/public-health/services/diseases/gonorrhea.html" },
    ],
  },

  {
    id: "syphilis",
    slug: "syphilis",
    title: "Syphilis",
    subtitle: "Curable at every stage — but stages matter",
    kicker: "Bacterial STI",
    icon: "AlertTriangle",
    tone: "orange",
    hubBlurb:
      "A curable bacterial STI with distinct stages. Rates are climbing in Canada, and testing is important because early sores can be painless and easy to miss.",
    sections: [
      {
        heading: "What it is",
        body:
          "Syphilis is a bacterial infection caused by Treponema pallidum. It moves through stages if untreated: primary (a painless sore), secondary (rash and flu-like symptoms), latent (no symptoms), and late (which can affect the heart, brain, and other organs). It is curable at every stage with the right antibiotics.",
      },
      {
        heading: "Common symptoms",
        body:
          "The first sign is usually a single painless sore (chancre) where the bacteria entered the body — often the genitals, mouth, or rectum. Later, some people get a rash on the palms and soles, patchy hair loss, sore throat, swollen glands, or flu-like feelings. Symptoms come and go, which can be confusing.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "Because the first sore is painless and can be hidden (inside the vagina, rectum, or throat), it's easy to miss. Latent syphilis has no symptoms at all but can still be passed on and can still cause damage over time.",
      },
      {
        heading: "How it spreads",
        body:
          "Through direct contact with a syphilis sore during vaginal, anal, or oral sex. It can also pass from a pregnant person to their baby (congenital syphilis), which can be serious — prenatal testing is important.",
      },
      {
        heading: "Testing",
        body:
          "A blood test detects syphilis. If a sore is present, a swab can also be tested. Syphilis is part of standard STI screening in Canada, and prenatal syphilis testing is routine in most provinces.",
      },
      {
        heading: "Treatment",
        body:
          "Syphilis is cured with penicillin injections. The number of doses depends on the stage. Follow-up blood tests are used to confirm the treatment worked.",
      },
      {
        heading: "Prevention",
        body:
          "Condoms lower risk but don't fully prevent it if a sore is outside the covered area. Regular testing, prompt treatment, and partner notification are the strongest tools.",
      },
      {
        heading: "When to seek medical care",
        body:
          "Get tested if you have any unexplained sore, rash, or a recent exposure — and as part of routine screening if you're sexually active. Anyone pregnant should be tested early in pregnancy and again if there's ongoing risk.",
      },
      {
        heading: "Talking with partners",
        body:
          "Recent partners (going back up to a year or more depending on the stage) should be notified so they can test and treat. Public health teams help with this if you'd rather not do it yourself.",
      },
    ],
    sources: [
      { label: "Government of Canada — Syphilis", url: "https://www.canada.ca/en/public-health/services/diseases/syphilis.html" },
    ],
  },

  {
    id: "hpv",
    slug: "hpv",
    title: "HPV (Human Papillomavirus)",
    subtitle: "Extremely common — often clears on its own, and now vaccine-preventable",
    kicker: "Viral STI",
    icon: "ShieldCheck",
    tone: "violet",
    hubBlurb:
      "HPV is very common. Most infections clear on their own, some cause warts, and a few high-risk types can cause cancer. HPV vaccination can prevent many cancers caused by high-risk HPV types.",
    sections: [
      {
        heading: "What it is",
        body:
          "HPV is a group of over 100 related viruses. Some cause genital warts; a small number (called high-risk types) can lead to cancers of the cervix, anus, penis, vulva, vagina, or throat if the infection persists. Most sexually active people get HPV at some point.",
      },
      {
        heading: "Common symptoms",
        body:
          "Many HPV infections cause no symptoms. Some types cause genital warts — soft, flesh-coloured bumps in the genital or anal area. High-risk types usually cause no visible symptoms but can be detected on a cervical screening test.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "Most people who get HPV never know they had it. The immune system clears most infections within a year or two.",
      },
      {
        heading: "How it spreads",
        body:
          "Through skin-to-skin genital contact — vaginal, anal, or oral sex. Because it spreads by skin contact, condoms lower the risk but don't fully prevent it.",
      },
      {
        heading: "Testing",
        body:
          "There is no routine 'HPV test' for people with a penis. For people with a cervix, cervical screening (Pap test and/or HPV test) checks for changes caused by high-risk HPV. Follow your province's screening guidelines. Warts are diagnosed by visual exam.",
      },
      {
        heading: "Treatment and management",
        body:
          "There's no cure for HPV itself — most infections clear naturally. Warts can be treated with creams, freezing, or minor procedures. Precancerous cell changes found through screening can be monitored or treated so they don't develop into cancer.",
      },
      {
        heading: "Vaccination",
        body:
          "The HPV vaccine is safe, effective, and protects against the most common cancer-causing and wart-causing types. It's part of routine school programs in Canada and is available for adults up to age 45 through a healthcare provider. Vaccination is most effective before HPV exposure but can still be helpful later.",
      },
      {
        heading: "Prevention",
        body:
          "Vaccination is the most powerful prevention. Condoms and dental dams lower risk. Regular cervical screening detects changes early.",
      },
      {
        heading: "When to seek medical care",
        body:
          "See a provider for new bumps or growths in the genital area, unusual bleeding, or if you're due for cervical screening. If you're not yet vaccinated and are under 45, ask about the vaccine.",
      },
    ],
    sources: [
      { label: "Government of Canada — Human papillomavirus (HPV)", url: "https://www.canada.ca/en/public-health/services/diseases/human-papillomavirus-hpv.html" },
      { label: "Government of Canada — HPV vaccine", url: "https://www.canada.ca/en/public-health/services/vaccination-children/human-papillomavirus-hpv-vaccine.html" },
    ],
  },

  {
    id: "hepatitis",
    slug: "hepatitis",
    title: "Hepatitis B & C",
    subtitle: "Preventable, treatable, and often curable",
    kicker: "Viral infections",
    icon: "Droplet",
    tone: "yellow",
    hubBlurb:
      "Two different viruses that affect the liver. Hepatitis B is vaccine-preventable; hepatitis C is curable with modern treatment.",
    sections: [
      {
        heading: "What they are",
        body:
          "Hepatitis B (HBV) and hepatitis C (HCV) are viral infections that affect the liver. They are different viruses with different treatments, but both can be spread through blood and sexual contact, and both can be silent for years.",
      },
      {
        heading: "Common symptoms",
        body:
          "Many people have no symptoms for a long time. When symptoms occur they can include fatigue, nausea, belly pain, dark urine, yellowing of the skin or eyes (jaundice), joint pain, or loss of appetite.",
      },
      {
        heading: "Many people have no symptoms",
        body:
          "Both HBV and HCV can be chronic (long-term) with no obvious signs. Testing is the only reliable way to know — and modern treatment works better when started before major liver damage.",
      },
      {
        heading: "How they spread",
        body:
          "Hepatitis B spreads through blood, sexual fluids, and from parent to baby at birth. Hepatitis C spreads mainly through blood — for example, sharing needles or drug equipment, or through certain sexual practices with blood exposure. Neither is spread by hugging, sharing food, or casual contact.",
      },
      {
        heading: "Testing",
        body:
          "Blood tests diagnose both. In Canada, testing is available through primary care, sexual health clinics, and community organizations. Anyone born between 1945 and 1975, or with any risk factor, is generally encouraged to be tested at least once for hepatitis C.",
      },
      {
        heading: "Treatment",
        body:
          "Hepatitis B can be managed long-term with antiviral medicines. Hepatitis C can be cured for most people with 8–12 weeks of pills (direct-acting antivirals). Treatment access in Canada is broad — talk to a healthcare provider or a hepatitis-focused clinic.",
      },
      {
        heading: "Vaccination",
        body:
          "There is a safe, effective vaccine for hepatitis B — often given in childhood, and available to adults who aren't yet vaccinated. There is no vaccine for hepatitis C, but it is curable.",
      },
      {
        heading: "Prevention",
        body:
          "Hepatitis B vaccination, safer sex, and not sharing needles, syringes, or drug equipment. For hepatitis C, harm-reduction supplies (new equipment every time) are the key prevention.",
      },
      {
        heading: "When to seek medical care",
        body:
          "Get tested if you've ever shared needles or drug equipment, had a blood exposure, or if it's been years since your last screen. Anyone pregnant is usually tested for hepatitis B; ask about hepatitis C too.",
      },
    ],
    sources: [
      { label: "Government of Canada — Hepatitis B", url: "https://www.canada.ca/en/public-health/services/diseases/hepatitis-b.html" },
      { label: "Government of Canada — Hepatitis C", url: "https://www.canada.ca/en/public-health/services/diseases/hepatitis-c.html" },
      { label: "CATIE — Hepatitis C information", url: "https://www.catie.ca/hepatitis-c-basics" },
    ],
  },

  {
    id: "testing",
    slug: "testing",
    title: "STI Testing",
    subtitle: "What to ask for, how it works, and where to go in Canada",
    kicker: "Testing",
    icon: "Stethoscope",
    tone: "teal",
    hubBlurb:
      "A practical guide to STI testing in Canada — what STI testing may include, how to ask for testing, and where to go.",
    sections: [
      {
        heading: "Why testing matters",
        body:
          "Most STIs have no symptoms much of the time. Testing is the only way to know your status. It's a routine part of taking care of yourself and your partners — not a sign that anything is wrong.",
      },
      {
        heading: "What STI testing may include",
        body:
          "A typical Canadian STI screen includes chlamydia, gonorrhea, syphilis, and HIV. Depending on your situation, providers may also offer hepatitis B, hepatitis C, and HSV testing. HPV is usually checked as part of cervical screening.",
      },
      {
        heading: "How the tests are done",
        body:
          "Depending on the STI, tests use blood, urine, or a swab (throat, vaginal, or rectal). Testing at all sites where you have sex — not just a urine test — is important because some infections only show up in the throat or rectum.",
      },
      {
        heading: "How often to test",
        body:
          "A general starting point: at least once a year if you're sexually active, more often (every 3–6 months) if you have new or multiple partners, or based on your provider's advice. If you had a possible exposure, ask about the window period for each infection.",
      },
      {
        heading: "Where to go",
        body:
          "Options in Canada include: your family doctor or nurse practitioner, sexual health / STI clinics (many are free and drop-in), Planned Parenthood locations, community health centres, university/college health services, and — in many provinces — free at-home test kits ordered online through public health.",
      },
      {
        heading: "How to ask",
        body:
          "You can simply say: 'I'd like a full STI screen, including tests for all the sites where I have sex.' You don't have to explain your sexual history in detail unless you want to. A good provider will meet you without judgment.",
      },
      {
        heading: "Cost and privacy",
        body:
          "Most STI testing in Canada is covered by provincial health plans and is confidential. Anonymous HIV testing is available in most provinces. You do not need to share test results with anyone unless you choose to.",
      },
      {
        heading: "What if a test comes back positive",
        body:
          "Take a breath — a positive result is health information, not a moral judgment. Most STIs are curable and all are manageable. A clinician will explain treatment or next steps, and can help with partner notification.",
      },
    ],
    sources: [
      { label: "Government of Canada — Sexually transmitted infections", url: "https://www.canada.ca/en/public-health/services/diseases/sexually-transmitted-infections.html" },
      { label: "Government of Canada — Get tested for STIs", url: "https://www.canada.ca/en/public-health/services/diseases/sexually-transmitted-infections/get-tested.html" },
    ],
  },

  {
    id: "safer-sex",
    slug: "safer-sex",
    title: "Safer Sex",
    subtitle: "Tools and habits that lower risk without killing the mood",
    kicker: "Prevention",
    icon: "Shield",
    tone: "sage",
    hubBlurb:
      "Condoms, dental dams, lube, vaccines, PrEP, communication — a practical toolbox for lowering STI risk together.",
    sections: [
      {
        heading: "What 'safer sex' means",
        body:
          "Safer sex is a set of choices — not a single rule — that lowers the chance of passing or getting STIs. Different people mix these tools differently, and that's fine. The goal is not zero risk; it's informed choice with the people you're intimate with.",
      },
      {
        heading: "Condoms and dental dams",
        body:
          "External (penis) condoms, internal condoms, and dental dams create a barrier against fluid and skin contact. They're highly effective at lowering the risk of HIV, chlamydia, gonorrhea, and hepatitis B; they also help with syphilis, HSV, and HPV where the barrier covers the area. Use a fresh one each time and check the expiry date.",
      },
      {
        heading: "Lubricant",
        body:
          "Good lube makes sex more comfortable and reduces microtears in skin, which lowers infection risk. Use water-based or silicone-based lube with latex condoms. Oil-based products (baby oil, lotion, coconut oil) break latex.",
      },
      {
        heading: "Vaccines",
        body:
          "The HPV vaccine and the hepatitis B vaccine are safe, effective, and part of routine care. Hepatitis A vaccination is also recommended for some people. Ask your provider whether you're up to date.",
      },
      {
        heading: "PrEP and PEP",
        body:
          "PrEP is a pill (or in some cases an injection) that HIV-negative people take to prevent HIV. PEP is emergency medicine started within 72 hours of a possible HIV exposure. Both are available in Canada — see the HIV page for details.",
      },
      {
        heading: "Regular testing",
        body:
          "Because many STIs have no symptoms, testing on a regular schedule is one of the strongest safer-sex tools. See the STI Testing page for what to ask for and how often.",
      },
      {
        heading: "Communication and consent",
        body:
          "Talking about status, testing, protection, and boundaries is part of safer sex — not something separate from it. A partner who reacts to that conversation with care is telling you something important about them.",
      },
      {
        heading: "Harm reduction, not shame",
        body:
          "If a barrier slips, a plan changes, or something happens you didn't intend — testing, PEP, and follow-up are there for you. Health care providers see this every day. There's no version of sexual health where shame helps.",
      },
    ],
    sources: [
      { label: "Government of Canada — Condoms, gloves, dams", url: "https://www.canada.ca/en/public-health/services/sex-your-health/condoms-gloves-dams.html" },
      { label: "Government of Canada — Sex and your health", url: "https://www.canada.ca/en/public-health/services/sex-your-health.html" },
    ],
  },

  {
    id: "disclosure",
    slug: "disclosure",
    title: "Disclosure & Dating",
    subtitle: "Talking about status without turning it into an apology",
    kicker: "Relationships",
    icon: "MessageCircleHeart",
    tone: "rose",
    hubBlurb:
      "Practical, warm guidance for talking about STI status with partners — timing, wording, and what to do when a conversation goes well (or doesn't).",
    sections: [
      {
        heading: "There is no single right way",
        body:
          "Disclosure is personal. Some people share early, before things get physical; others wait until they know they like someone. There is no rule — only what feels honest and safe for you.",
      },
      {
        heading: "When to talk",
        body:
          "A common approach: before any sexual contact where an STI could be passed. Some people feel most comfortable in a calm, private moment — a walk, a quiet coffee — rather than in the middle of a physical moment.",
      },
      {
        heading: "What to say",
        body:
          "You don't need a speech. Something like: 'Before we go further, I want to share something. I have [HSV / HPV / HIV / etc.]. Here's what I've learned about it and how I manage it. I'm happy to answer questions.' Facts + calm > apology + drama.",
      },
      {
        heading: "Give accurate information",
        body:
          "Many people react from movies, playgrounds, or bad Google searches. A short factual summary — how it's spread, how you manage it, how partners protect themselves — reframes the conversation. Point them to a reputable source (like Public Health Agency of Canada) rather than doing all the educating alone.",
      },
      {
        heading: "Let them react — and take their time",
        body:
          "It's okay for a partner to have questions, need a day to think, or ask you again later. It is also okay for them to say no — that's a compatibility answer, not a verdict on your worth. Most reactions are better than people fear.",
      },
      {
        heading: "If disclosure doesn't go well",
        body:
          "You are the same person you were before you told them. If someone reacts with cruelty, that's information about them. Talk to a friend, a therapist, or people in the HerpMates community who understand.",
      },
      {
        heading: "Disclosure and the law in Canada",
        body:
          "For HIV specifically, Canadian law can require disclosure in certain circumstances. If you're living with HIV, an organization like CATIE or a legal clinic can walk you through what applies to your situation. For other STIs the legal picture is different — a sexual health clinic can advise.",
      },
      {
        heading: "You are not your diagnosis",
        body:
          "STIs are health information. They aren't a personality, a moral standing, or a story about who you can be to someone. HerpMates exists so this part gets easier — every member has already made peace with this conversation in their own way.",
      },
    ],
    sources: [
      { label: "CATIE — HIV disclosure and the law", url: "https://www.catie.ca/hiv-disclosure-a-legal-guide-for-people-living-with-hiv-in-canada" },
      { label: "Government of Canada — Sexually transmitted infections", url: "https://www.canada.ca/en/public-health/services/diseases/sexually-transmitted-infections.html" },
    ],
  },
];

export const DISCLAIMER =
  "HerpMates is not a medical provider. This information is for educational purposes only and is not a diagnosis or substitute for medical care. If you are concerned about an STI, contact a healthcare provider or sexual health clinic.";

export function findTopic(slug) {
  return TOPICS.find((t) => t.slug === slug) || null;
}
